(() => {
  'use strict';

  const q = (selector, context = document) => context ? context.querySelector(selector) : null;
  const qa = (selector, context = document) => context ? Array.from(context.querySelectorAll(selector)) : [];

  const formatMoney = (cents, currency = 'USD') => {
    const amount = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency,
        minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
        maximumFractionDigits: 2
      }).format(amount);
    } catch (_) {
      return `$${amount.toFixed(2)}`;
    }
  };

  const initSliders = (scope = document) => {
    qa('[data-elaa-slider]', scope).forEach((slider) => {
      if (slider.dataset.elaaInitialized === 'true') return;
      slider.dataset.elaaInitialized = 'true';
      const track = q('[data-track]', slider);
      if (!track) return;

      const advance = (direction = 1) => {
        const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 12;
        if (direction > 0 && atEnd) {
          track.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          track.scrollBy({ left: direction * track.clientWidth * 0.85, behavior: 'smooth' });
        }
      };

      qa('[data-prev]', slider).forEach((button) => button.addEventListener('click', () => advance(-1)));
      qa('[data-next]', slider).forEach((button) => button.addEventListener('click', () => advance(1)));

      const delay = Number(slider.dataset.elaaAutoplay || 0);
      const desktop = window.matchMedia('(min-width: 990px)');
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
      let timer = null;
      let paused = false;

      const stop = () => {
        if (timer) window.clearInterval(timer);
        timer = null;
      };
      const start = () => {
        stop();
        if (delay > 0 && desktop.matches && !reduced.matches && !paused) {
          timer = window.setInterval(() => advance(1), delay);
        }
      };

      slider.addEventListener('pointerenter', () => { paused = true; stop(); });
      slider.addEventListener('pointerleave', () => { paused = false; start(); });
      slider.addEventListener('focusin', () => { paused = true; stop(); });
      slider.addEventListener('focusout', () => { paused = false; start(); });
      desktop.addEventListener?.('change', start);
      reduced.addEventListener?.('change', start);
      start();
    });
  };

  const initMobileDrawer = (scope = document) => {
    const drawer = q('[data-mobile-drawer]', scope) || q('[data-mobile-drawer]');
    const openButton = q('[data-mobile-open]', scope) || q('[data-mobile-open]');
    const closeButton = drawer ? q('[data-mobile-close]', drawer) : null;
    if (!drawer || !openButton || drawer.dataset.elaaInitialized === 'true') return;
    drawer.dataset.elaaInitialized = 'true';

    const setDrawer = (open) => {
      drawer.classList.toggle('is-open', open);
      drawer.setAttribute('aria-hidden', open ? 'false' : 'true');
      openButton.setAttribute('aria-expanded', open ? 'true' : 'false');
      document.body.classList.toggle('no-scroll', open);
      if (open) closeButton?.focus();
    };

    openButton.addEventListener('click', () => setDrawer(true));
    closeButton?.addEventListener('click', () => setDrawer(false));
    drawer.addEventListener('click', (event) => {
      if (event.target === drawer) setDrawer(false);
      if (event.target.closest('a')) setDrawer(false);
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && drawer.classList.contains('is-open')) setDrawer(false);
    });
  };

  const normalizeHeading = (value) => (value || '')
    .replace(/\u00a0/g, ' ')
    .replace(/[–—]/g, '-')
    .replace(/[“”]/g, '"')
    .replace(/[’‘]/g, "'")
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/[:.]+$/, '')
    .toLowerCase();

  const isStandaloneBoldHeading = (element) => {
    if (!element || element.nodeType !== 1 || element.tagName !== 'P') return false;
    const text = (element.textContent || '').trim();
    if (!text || text.length > 170) return false;
    const meaningfulChildren = Array.from(element.childNodes).filter((node) => {
      if (node.nodeType === Node.TEXT_NODE) return node.textContent.trim().length > 0;
      return true;
    });
    if (meaningfulChildren.length !== 1) return false;
    const child = meaningfulChildren[0];
    return child.nodeType === 1 && ['STRONG', 'B'].includes(child.tagName);
  };

  const isHeadingElement = (element) => {
    if (!element || element.nodeType !== 1) return false;
    return /^H[1-6]$/.test(element.tagName) || isStandaloneBoldHeading(element);
  };

  const classifyHeading = (heading) => {
    const key = normalizeHeading(heading);
    if (!key) return null;

    if (
      key === 'results' ||
      key === 'key features and formula details' ||
      key === 'what it is formulated to do' ||
      /^what .+ is formulated to do$/.test(key) ||
      /^what .+ formulated to do$/.test(key)
    ) return 'What It Is Formulated to Do';

    if (
      key === 'best for' ||
      key === "who it's for" ||
      key === 'who it is for' ||
      key.startsWith('created for ') ||
      key.startsWith('designed for ') ||
      key.startsWith('designed with ') ||
      key.startsWith('choose your ') ||
      key.startsWith('choosing your ') ||
      key.startsWith('how to choose ') ||
      key.startsWith('created with ') ||
      key.startsWith('created for most ') ||
      key.startsWith('created for mature ') ||
      key.startsWith('created for supple ') ||
      key.startsWith('created for sensitive ') ||
      key.startsWith('essential-oil-free care for ') ||
      key.startsWith('designed with reactive ') ||
      key === 'can you use more than one' ||
      key === 'a generous family size' ||
      key === 'choosing your elaa cleanser' ||
      key === 'how to choose your elaa cleanser'
    ) return 'Who It’s For';

    if (
      key === 'the elaa ritual' ||
      key === 'elaa ritual' ||
      key.startsWith('the elaa ritual -') ||
      key.startsWith('the elaa ritual &') ||
      key === 'how to use' ||
      key === 'directions' ||
      key === 'application' ||
      key === 'frequency'
    ) return 'The Elaa Ritual';

    // Only explicit section-level aliases start the Activated Oxygen bucket.
    // Ingredient and usage subheadings such as “Fully Ozonated Olive Oil,”
    // “As an ozonated toothpaste,” or “Activated Oxygen — Blue Tansy” must
    // remain inside the section in which the merchant placed them. A broad
    // substring match here caused About the Ingredients to be swallowed by
    // the Activated Oxygen accordion on products such as After The Rain.
    if (
      key === 'activated oxygen' ||
      key === 'activated-oxygen' ||
      key === 'about activated oxygen' ||
      key === 'about ozone' ||
      key === 'about ozonated oil' ||
      key === 'the activated oxygen experience' ||
      key === 'the activated-oxygen experience'
    ) return 'Activated Oxygen';

    if (
      key === 'about the ingredients' ||
      key === 'about ingredients' ||
      key === 'ingredient highlights' ||
      key === 'hero ingredients'
    ) return 'About the Ingredients';

    if (
      key === 'ingredients' ||
      key === 'complete ingredients' ||
      key === 'full strength ingredients' ||
      key === 'ultra sensitive ingredients' ||
      key.startsWith('ingredients -') ||
      key.startsWith('ingredients —') ||
      (key.endsWith(' ingredients') && !key.startsWith('about the') && !key.startsWith('key'))
    ) return 'Complete Ingredients';

    if (
      key.startsWith('product care') ||
      key.includes('freshly handcrafted') ||
      key.startsWith('heat, melting') ||
      key.startsWith('heat and storage') ||
      key.startsWith('heat sensitive') ||
      key.startsWith('storage') ||
      key.includes('melting and storage') ||
      key === 'a note about the color' ||
      key === 'note about the color' ||
      key === 'bee product information' ||
      key === 'research references' ||
      key === 'safety information'
    ) return 'Product Care';

    return null;
  };

  const createSubheading = (element) => {
    const subheading = document.createElement('h3');
    subheading.className = 'elaa-product-accordion__subheading';
    subheading.innerHTML = element.innerHTML;
    return subheading;
  };


  const productLabels = (productSection) => {
    if (productSection._elaaLabels) return productSection._elaaLabels;
    const defaults = {
      add: 'ADD TO BAG',
      soldOut: 'SOLD OUT',
      benefits: 'What It Is Formulated to Do',
      who: 'Who It’s For',
      ritual: 'The Elaa Ritual',
      oxygen: 'Activated Oxygen',
      aboutIngredients: 'About the Ingredients',
      ingredients: 'Complete Ingredients',
      care: 'Product Care'
    };
    const node = q('[data-elaa-product-labels]', productSection);
    try {
      productSection._elaaLabels = { ...defaults, ...(node ? JSON.parse(node.textContent) : {}) };
    } catch (_) {
      productSection._elaaLabels = defaults;
    }
    return productSection._elaaLabels;
  };

  const initProductDescription = (productSection) => {
    const template = q('[data-elaa-description-template]', productSection);
    const intro = q('[data-elaa-description-intro]', productSection);
    const accordions = q('[data-elaa-product-accordions]', productSection);
    if (!template || !intro || !accordions || productSection.dataset.descriptionInitialized === 'true') return;
    productSection.dataset.descriptionInitialized = 'true';

    const productTitle = normalizeHeading(productSection.dataset.productTitle || '');
    const labels = productLabels(productSection);
    const wrapper = document.createElement('div');
    wrapper.appendChild(template.content.cloneNode(true));

    const order = [
      'What It Is Formulated to Do',
      'Who It’s For',
      'The Elaa Ritual',
      'Activated Oxygen',
      'About the Ingredients',
      'Complete Ingredients',
      'Product Care'
    ];
    const groups = Object.fromEntries(order.map((name) => [name, []]));
    const introNodes = [];
    let currentBucket = null;
    let encounteredCanonicalHeading = false;

    Array.from(wrapper.childNodes).forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        if (!node.textContent.trim()) return;
        const paragraph = document.createElement('p');
        paragraph.textContent = node.textContent.trim();
        (currentBucket ? groups[currentBucket] : introNodes).push(paragraph);
        return;
      }
      if (node.nodeType !== Node.ELEMENT_NODE) return;
      const text = (node.textContent || '').replace(/\s+/g, ' ').trim();
      if (!text) return;

      if (isHeadingElement(node)) {
        const normalized = normalizeHeading(text);
        if (normalized === productTitle || productTitle.startsWith(normalized) || normalized.startsWith(productTitle)) {
          return;
        }

        // Product descriptions often begin with one bold lead sentence. Keep that
        // lead visible above the purchase panel rather than treating it as an accordion.
        if (!encounteredCanonicalHeading && !currentBucket && introNodes.length === 0 && isStandaloneBoldHeading(node)) {
          const lead = document.createElement('p');
          lead.innerHTML = node.innerHTML;
          introNodes.push(lead);
          return;
        }

        const bucket = classifyHeading(text);
        if (bucket) {
          currentBucket = bucket;
          encounteredCanonicalHeading = true;
          const canonicalHeading = normalizeHeading(bucket);
          const genericHeading = [
            'results',
            'key features and formula details',
            'best for',
            "who it's for",
            'the elaa ritual',
            'elaa ritual',
            'activated oxygen',
            'about the ingredients',
            'ingredients',
            'complete ingredients',
            'product care',
            'product care and freshly handcrafted information',
            'product care & freshly handcrafted'
          ].includes(normalized);
          if (!genericHeading && normalized !== canonicalHeading && !(bucket === 'What It Is Formulated to Do' && normalized.includes('formulated to do'))) {
            groups[currentBucket].push(createSubheading(node));
          }
          return;
        }

        if (encounteredCanonicalHeading && currentBucket) {
          groups[currentBucket].push(createSubheading(node));
        } else if (/^H[1-6]$/.test(node.tagName)) {
          // Once the concise opening paragraphs are complete, place any editorial
          // feature story beneath the first benefits accordion instead of allowing a
          // very long uncollapsed product introduction.
          currentBucket = 'What It Is Formulated to Do';
          encounteredCanonicalHeading = true;
          groups[currentBucket].push(createSubheading(node));
        } else {
          introNodes.push(createSubheading(node));
        }
        return;
      }

      const lower = normalizeHeading(text);
      if (
        lower.includes('store in a cool, dry place') ||
        lower.includes('keep the container tightly closed') ||
        lower.includes('use a clean utensil') ||
        lower.includes('avoid introducing water') ||
        lower.includes('natural variations in') ||
        lower.includes('for greatest freshness')
      ) {
        groups['Product Care'].push(node.cloneNode(true));
        return;
      }

      (currentBucket ? groups[currentBucket] : introNodes).push(node.cloneNode(true));
    });

    intro.replaceChildren(...introNodes);
    accordions.replaceChildren();

    order.forEach((name) => {
      if (!groups[name].length) return;
      const details = document.createElement('details');
      details.className = 'elaa-product-accordion';
      const summary = document.createElement('summary');
      const displayLabels = {
        'What It Is Formulated to Do': labels.benefits,
        'Who It’s For': labels.who,
        'The Elaa Ritual': labels.ritual,
        'Activated Oxygen': labels.oxygen,
        'About the Ingredients': labels.aboutIngredients,
        'Complete Ingredients': labels.ingredients,
        'Product Care': labels.care
      };
      const heading = document.createElement('h2');
      heading.className = 'elaa-product-accordion__title';
      heading.textContent = displayLabels[name] || name;
      summary.replaceChildren(heading);
      const content = document.createElement('div');
      content.className = 'elaa-product-accordion__content rte';
      groups[name].forEach((node) => content.appendChild(node));
      details.append(summary, content);
      accordions.appendChild(details);
      if (productSection.dataset.openFirstAccordion === 'true' && accordions.children.length === 1) details.open = true;
    });
  };

  const activateMedia = (productSection, mediaId) => {
    if (!mediaId) return;
    qa('[data-media-panel]', productSection).forEach((panel) => {
      const active = String(panel.dataset.mediaPanel) === String(mediaId);
      panel.hidden = !active;
      panel.classList.toggle('is-active', active);
    });
    qa('[data-media-thumb]', productSection).forEach((thumb) => {
      const active = String(thumb.dataset.mediaThumb) === String(mediaId);
      thumb.classList.toggle('is-active', active);
      thumb.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  };

  const initProductGallery = (productSection) => {
    if (productSection.dataset.galleryInitialized === 'true') return;
    productSection.dataset.galleryInitialized = 'true';
    qa('[data-media-thumb]', productSection).forEach((thumb) => {
      thumb.addEventListener('click', () => activateMedia(productSection, thumb.dataset.mediaThumb));
    });
  };

  const initQuantity = (productSection) => {
    qa('[data-quantity-control]', productSection).forEach((control) => {
      if (control.dataset.elaaInitialized === 'true') return;
      control.dataset.elaaInitialized = 'true';
      const input = q('[data-quantity-input]', control);
      if (!input) return;
      q('[data-quantity-minus]', control)?.addEventListener('click', () => {
        const min = Number(input.min || 1);
        input.value = String(Math.max(min, Number(input.value || min) - 1));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
      q('[data-quantity-plus]', control)?.addEventListener('click', () => {
        const max = input.max ? Number(input.max) : Number.POSITIVE_INFINITY;
        input.value = String(Math.min(max, Number(input.value || 1) + 1));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  };

  const initProductPurchase = (productSection) => {
    if (productSection.dataset.purchaseInitialized === 'true') return;
    productSection.dataset.purchaseInitialized = 'true';
    const variantControl = q('[data-variant-select]', productSection);
    const price = q('[data-product-price]', productSection);
    const addButton = q('[data-add-to-cart]', productSection);
    const compare = q('[data-product-compare]', productSection);
    const planInput = q('[data-selling-plan-input]', productSection);
    const currency = productSection.dataset.currency || 'USD';
    const labels = productLabels(productSection);

    const selectedOption = () => {
      if (!variantControl) return null;
      if (variantControl.tagName === 'SELECT') return variantControl.options[variantControl.selectedIndex];
      return variantControl;
    };

    const activePlanFieldset = (variantId) => {
      let active = null;
      qa('[data-selling-plan-variant]', productSection).forEach((fieldset) => {
        const match = String(fieldset.dataset.sellingPlanVariant) === String(variantId);
        fieldset.hidden = !match;
        if (match) active = fieldset;
      });
      return active;
    };

    const updatePurchaseState = () => {
      const option = selectedOption();
      if (!option) return;
      const variantId = option.value;
      const available = option.dataset.available !== 'false';
      const fieldset = activePlanFieldset(variantId);
      let selectedPlan = fieldset ? q('[data-selling-plan-choice]:checked', fieldset) : null;
      if (fieldset && !selectedPlan) {
        selectedPlan = q('[data-selling-plan-choice]', fieldset);
        if (selectedPlan) selectedPlan.checked = true;
      }
      if (planInput) planInput.value = selectedPlan?.value || '';

      const cents = Number(selectedPlan?.dataset.price || option.dataset.price || 0);
      const compareCents = Number(selectedPlan?.dataset.comparePrice || option.dataset.comparePrice || 0);
      if (price) price.textContent = formatMoney(cents, currency);
      window.ELAAWholesalePrices?.update(productSection, cents, Number(option.dataset.comparePrice || 0), formatMoney(Number(option.dataset.comparePrice || 0), currency));
      if (compare) {
        const showCompare = compareCents > cents;
        compare.hidden = !showCompare;
        compare.textContent = showCompare ? formatMoney(compareCents, currency) : '';
      }
      if (addButton) {
        addButton.disabled = !available;
        addButton.textContent = available ? labels.add : labels.soldOut;
      }
      if (option.dataset.mediaId) activateMedia(productSection, option.dataset.mediaId);
    };

    variantControl?.addEventListener('change', updatePurchaseState);
    qa('[data-selling-plan-choice]', productSection).forEach((choice) => choice.addEventListener('change', updatePurchaseState));
    updatePurchaseState();
  };

  const initProductSections = (scope = document) => {
    qa('[data-product-section]', scope).forEach((section) => {
      initProductDescription(section);
      initProductGallery(section);
      initQuantity(section);
      initProductPurchase(section);
    });
  };

  const initGiftRecipients = (scope = document) => {
    qa('[data-gift-recipient]', scope).forEach((recipient) => {
      if (recipient.dataset.elaaInitialized === 'true') return;
      recipient.dataset.elaaInitialized = 'true';
      const toggle = q('[data-gift-recipient-toggle]', recipient);
      const fieldsWrap = q('[data-gift-recipient-fields]', recipient);
      const fields = qa('[data-gift-recipient-field]', recipient);
      const email = q('input[name="properties[Recipient email]"]', recipient);
      const offset = q('[data-gift-recipient-offset]', recipient);
      const sendOn = q('[data-gift-recipient-send-on]', recipient);

      const localDate = (date) => {
        const copy = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
        return copy.toISOString().slice(0, 10);
      };

      if (sendOn) {
        const today = new Date();
        const max = new Date(today);
        max.setDate(max.getDate() + 90);
        sendOn.min = localDate(today);
        sendOn.max = localDate(max);
      }
      if (offset) offset.value = String(new Date().getTimezoneOffset());

      const update = () => {
        const enabled = Boolean(toggle?.checked);
        if (fieldsWrap) fieldsWrap.hidden = !enabled;
        toggle?.setAttribute('aria-expanded', enabled ? 'true' : 'false');
        fields.forEach((field) => { field.disabled = !enabled; });
        if (email) email.required = enabled;
      };
      const form = recipient.closest('form');
      const submit = form ? q('[data-add-to-cart]', form) : null;
      const errorBox = q('[data-gift-recipient-errors]', recipient);
      const errorText = q('[data-gift-recipient-error-text]', recipient);
      const checkBag = q('[data-gift-check-bag]', recipient);
      let submitting = false;
      let uncertain = false;

      const showError = (message, checkCart = false) => {
        if (errorText) errorText.textContent = message;
        if (checkBag) checkBag.hidden = !checkCart;
        if (errorBox) {
          errorBox.hidden = false;
          errorBox.focus();
        }
      };
      const errorMessage = (value) => {
        if (typeof value === 'string') return value;
        if (Array.isArray(value)) return value.map(errorMessage).filter(Boolean).join(' ');
        if (value && typeof value === 'object') return Object.values(value).map(errorMessage).filter(Boolean).join(' ');
        return '';
      };

      form?.addEventListener('submit', async (event) => {
        if (!toggle?.checked && !uncertain && !submitting) return;
        event.preventDefault();
        if (submitting || uncertain) return;
        if (errorBox) errorBox.hidden = true;
        if (!form.reportValidity()) return;
        submitting = true;
        if (submit) submit.disabled = true;
        form.setAttribute('aria-busy', 'true');
        try {
          const response = await fetch(recipient.dataset.cartAddUrl, {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: new FormData(form)
          });
          const result = await response.json();
          if (!response.ok || result.status) {
            showError(errorMessage(result.errors || result.description || result.message) || recipient.dataset.giftError);
            return;
          }
          if (!result.id && !result.items?.length) throw new Error();
          uncertain = true;
          window.location.assign(recipient.dataset.cartUrl);
        } catch {
          uncertain = true;
          showError(recipient.dataset.giftUncertain, true);
        } finally {
          submitting = false;
          form.removeAttribute('aria-busy');
          if (submit && !uncertain) submit.disabled = false;
        }
      });

      toggle?.addEventListener('change', update);
      update();
    });
  };

  const initLoadMore = (scope = document) => {
    qa('[data-load-more]', scope).forEach((button) => {
      if (button.dataset.elaaInitialized === 'true') return;
      button.dataset.elaaInitialized = 'true';
      button.addEventListener('click', () => {
        const grid = q('[data-media-grid]', button.closest('section'));
        qa('[hidden]', grid).slice(0, 18).forEach((item) => { item.hidden = false; });
        if (!q('[hidden]', grid)) button.hidden = true;
      });
    });
  };



  const initReviewExpanders = (scope = document) => {
    qa('[data-elaa-review-card]', scope).forEach((card) => {
      if (card.dataset.elaaReviewInitialized === 'true') return;
      card.dataset.elaaReviewInitialized = 'true';
      const quote = q('[data-elaa-review-quote]', card);
      const toggle = q('[data-elaa-review-toggle]', card);
      if (!quote || !toggle) return;

      const updateVisibility = () => {
        if (card.classList.contains('is-expanded')) return;
        toggle.hidden = quote.scrollHeight <= quote.clientHeight + 3;
      };
      const setExpanded = (expanded) => {
        card.classList.toggle('is-expanded', expanded);
        toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        toggle.textContent = expanded ? toggle.dataset.lessLabel : toggle.dataset.moreLabel;
      };
      toggle.addEventListener('click', () => setExpanded(!card.classList.contains('is-expanded')));
      requestAnimationFrame(updateVisibility);
      window.addEventListener('resize', updateVisibility, { passive: true });
      document.fonts?.ready?.then(updateVisibility);
    });
  };

  const initAll = (scope = document) => {
    initSliders(scope);
    initMobileDrawer(scope);
    initProductSections(scope);
    initGiftRecipients(scope);
    initLoadMore(scope);
    initReviewExpanders(scope);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document));
  } else {
    initAll(document);
  }
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
